import React, { useMemo } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export default function ContentSection({
  /* PROJETO */
  companies = [],
  companiesLoading = false,
  formData,
  handleProjectChange,

  /* CATEGORIAS */
  categories,
  categoriesLoading = false,
  subcats,
  handleText,
  skuFieldError = "",
  handleCategoryChange,
  handleSubcatChange,
}) {
  const { t } = useTranslation("products");
  // Converte string "tag1, tag2" -> ["tag1", "tag2"]
  const tagsValue = useMemo(() => {
    const raw = formData.tags || "";
    return String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [formData.tags]);

  // Atualiza formData.tags como string, mantendo compatibilidade
  const handleTagsChange = (_event, newValue) => {
    const str = (newValue || [])
      .map((v) => (typeof v === "string" ? v : v?.label || ""))
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");

    handleText({
      target: {
        name: "tags",
        value: str,
      },
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={t("form.contentCardTitle")}
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Projeto */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={companies}
              getOptionLabel={(o) => o?.nome || o?.name || ""}
              value={
                companies.find((c) => String(c.id) === String(formData.ids_projetos)) || null
              }
              onChange={handleProjectChange}
              loading={companiesLoading}
              loadingText={t("form.loadingCompanies")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.companyRequired")}
                  placeholder={t("form.selectCompany")}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tipo-label">{t("form.typeLabel")}</InputLabel>
              <Select
                labelId="tipo-label"
                label={t("form.typeLabel")}
                name="tipo"
                value={formData.tipo}
                onChange={handleText}
              >
                <MenuItem value={1}>{t("form.menuProduct")}</MenuItem>
                <MenuItem value={2}>{t("form.menuService")}</MenuItem>
                <MenuItem value={3}>{t("form.menuTicket")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={handleText}
              fullWidth
              placeholder={t("form.skuPlaceholder")}
              error={Boolean(skuFieldError)}
              helperText={skuFieldError || t("form.skuHelper")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("form.nameRequired")}
              name="nome"
              value={formData.nome}
              onChange={handleText}
              fullWidth
              required
              placeholder={t("form.namePlaceholder")}
            />
          </Grid>

          {/* TAGS com Autocomplete + chips */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]} // no futuro dá pra plugar sugestões de tags aqui
              value={tagsValue}
              onChange={handleTagsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.tagsLabel")}
                  placeholder={t("form.tagsPlaceholder")}
                  helperText={t("form.tagsHelper")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={categories}
              getOptionLabel={(o) => o?.nome || ""}
              value={
                categories.find((c) => String(c.id) === String(formData.id_categoria)) || null
              }
              onChange={handleCategoryChange}
              loading={categoriesLoading}
              loadingText={t("form.loadingCategories")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.categoryLabel")}
                  placeholder={t("form.selectEllipsis")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={subcats}
              getOptionLabel={(o) => o?.nome || ""}
              value={
                subcats.find((s) => String(s.id) === String(formData.id_subcategoria)) || null
              }
              onChange={handleSubcatChange}
              disabled={!subcats.length}
              loading={categoriesLoading}
              loadingText={t("form.loadingSubcategories")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.subcategoryLabel")}
                  placeholder={
                    subcats.length ? t("form.selectEllipsis") : t("form.subcategoryNone")
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
